import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useTranslation } from "../hooks/useTranslation";
import { useWallet } from "../hooks/useWallet";
import { API_BASE } from "../constants/index";

const formatAmount = (value) => {
    const n = Number(value || 0);
    if (!Number.isFinite(n)) return String(value ?? "0.00");
    return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const getRecordDate = (record) => (
    record.mo_cdate ||
    record.mo_created_at ||
    record.created_at ||
    record.mo_udate ||
    record.updated_at ||
    record.timestamp ||
    null
);

export default function YieldHistory() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { account, isConnected } = useWallet();
    const [records, setRecords] = useState([]);
    const [totalStake, setTotalStake] = useState("0.00");
    const [isLoading, setIsLoading] = useState(true);
    const [apiError, setApiError] = useState(null);
    const [timeRange, setTimeRange] = useState("All");
    const [currentPage, setCurrentPage] = useState(0);

    const pageSize = 10;

    const getTimeRangeBoundary = () => {
        if (timeRange === "All") return 0;
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
        switch (timeRange) {
            case "Today": return startOfToday;
            case "1W": return startOfToday - 7 * 86400000;
            case "1M": return startOfToday - 30 * 86400000;
            case "3M": return startOfToday - 90 * 86400000;
            default: return 0;
        }
    };

    const fetchYieldHistory = useCallback(async () => {
        if (!account || !isConnected) {
            setRecords([]);
            setTotalStake("0.00");
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setApiError(null);
        try {
            const response = await fetch(`${API_BASE}/publicApi/getYieldStakingRewards`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ userAddress: account }),
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok || !result.success) {
                throw new Error(result.error || result.message || "Failed to load yield history");
            }
            const list = result.stake_history ?? result.records ?? [];
            setTotalStake(String(result.total_stake ?? result.total ?? "0.00"));
            setRecords(Array.isArray(list) ? list : []);
        } catch (error) {
            console.error("Error loading yield history:", error);
            setApiError(error.message || "Failed to load yield history");
            setRecords([]);
            setTotalStake("0.00");
        } finally {
            setIsLoading(false);
        }
    }, [account, isConnected]);

    useEffect(() => {
        fetchYieldHistory();
    }, [fetchYieldHistory]);

    const filteredRecords = useMemo(() => records.filter((record) => {
        const minTime = getTimeRangeBoundary();
        if (!minTime) return true;
        const dateValue = getRecordDate(record);
        if (!dateValue) return false;
        const tMs = new Date(dateValue).getTime();
        if (Number.isNaN(tMs)) return false;
        return tMs >= minTime;
    }), [records, timeRange]);

    const totalPages = Math.max(1, Math.ceil(filteredRecords.length / pageSize));
    const safePage = Math.min(currentPage, totalPages - 1);
    const pageRecords = filteredRecords.slice(safePage * pageSize, safePage * pageSize + pageSize);

    return (
        <div className="relative overflow-x-hidden -mx-4 -my-8 min-h-[calc(100vh-200px)]">
            <div className="absolute inset-0 bg-gradient-to-b from-[#0a0610] via-[#241630] to-[#0a0610] -z-10" />
            <div className="relative z-10 pt-6 pb-6 px-4 max-w-md mx-auto space-y-6">
                <div className="flex items-center gap-4">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="flex items-center justify-center size-10 rounded-full text-white hover:bg-white/10 transition-colors"
                        aria-label={t("common.goBack") || "Go back"}
                    >
                        <span className="material-symbols-outlined text-2xl">arrow_back</span>
                    </button>
                    <div>
                        <h1 className="text-2xl font-semibold text-white">{t("yieldHistory.title") || "Yield History"}</h1>
                        <p className="text-sm text-[#ad90cb]">{t("yieldHistory.subtitle") || "VT + GRAM stake history"}</p>
                    </div>
                </div>

                <div className="rounded-3xl bg-[#1a1023]/70 backdrop-blur-md border border-[#9770DD]/30 p-5 shadow-lg shadow-[#9770DD]/15">
                    <p className="text-[#938F99] text-[12px] uppercase tracking-wider">{t("yieldHistory.totalStake") || "Total Stake"}</p>
                    <p className="text-white font-extrabold text-3xl mt-2">{formatAmount(totalStake)} <span className="text-[#7c6a9a] text-base">USDT</span></p>
                </div>

                <div className="rounded-3xl bg-[#1a1023]/70 backdrop-blur-md border border-[#9770DD]/30 p-5 shadow-lg shadow-[#9770DD]/15">
                    <div className="flex items-center justify-between gap-3 mb-5">
                        <div className="flex items-center gap-3">
                            <span className="material-symbols-outlined text-gray-300 text-2xl">history</span>
                            <h2 className="text-lg font-semibold text-white">{t("yieldHistory.records") || "Yield Records"}</h2>
                        </div>
                        <select
                            value={timeRange}
                            onChange={(e) => { setTimeRange(e.target.value); setCurrentPage(0); }}
                            className="rounded-lg bg-[#241630] border border-[#9770DD]/30 text-[#DFD2FE] text-xs px-2 py-2 outline-none"
                        >
                            {['All', 'Today', '1W', '1M', '3M'].map((item) => <option key={item} value={item}>{item}</option>)}
                        </select>
                    </div>

                    {apiError && <div className="mb-4 py-2 px-3 rounded-lg bg-amber-500/20 border border-amber-500/40 text-amber-200 text-sm">{apiError}</div>}

                    {isLoading ? (
                        <div className="py-12 text-center text-[#ad90cb]">
                            <span className="material-symbols-outlined animate-spin text-3xl">progress_activity</span>
                            <p className="mt-2">{t("common.loading") || "Loading..."}</p>
                        </div>
                    ) : filteredRecords.length === 0 ? (
                        <div className="py-12 text-center text-[#ad90cb]">
                            <span className="material-symbols-outlined text-4xl text-[#9770DD]/50">lock</span>
                            <p className="mt-3 text-sm">{t("yieldHistory.noRecords") || "No yield records yet"}</p>
                            <button
                                type="button"
                                onClick={() => navigate("/yield")}
                                className="mt-6 px-6 py-2.5 rounded-xl bg-[#9770DD] text-white text-sm font-semibold hover:bg-[#7a1fd6] transition-colors"
                            >
                                {t("yieldHistory.goToYield") || "Go to Yield"}
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {pageRecords.map((record, index) => {
                                const amount = record.mo_amount ?? record.amount ?? record.total ?? "0";
                                const status = record.mo_status ?? record.status ?? "active";
                                const dateValue = getRecordDate(record);
                                const dateStr = dateValue ? dayjs(dateValue).format("DD/MM/YYYY, HH:mm") : "—";
                                return (
                                    <div key={(record.mo_id || record.id) ?? index} className="py-3 px-4 rounded-xl bg-[#241630]/50 border border-[#9770DD]/20">
                                        <div className="flex items-center justify-between gap-3">
                                            <p className="text-white font-medium">{formatAmount(amount)} USDT</p>
                                            <span className="text-[11px] px-2 py-1 rounded-full bg-[#9770DD]/20 text-[#DFD2FE] uppercase">{status}</span>
                                        </div>
                                        <div className="flex items-center justify-between gap-3 mt-2 text-xs text-[#ad90cb]">
                                            <span>{dateStr}</span>
                                            <span>#{record.mo_id ?? record.id ?? '-'}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {filteredRecords.length > pageSize && (
                        <div className="flex items-center justify-between mt-5 text-sm text-[#ad90cb]">
                            <button type="button" disabled={safePage === 0} onClick={() => setCurrentPage((p) => Math.max(0, p - 1))} className="px-3 py-2 rounded-lg border border-[#9770DD]/30 disabled:opacity-40">Prev</button>
                            <span>{safePage + 1} / {totalPages}</span>
                            <button type="button" disabled={safePage >= totalPages - 1} onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))} className="px-3 py-2 rounded-lg border border-[#9770DD]/30 disabled:opacity-40">Next</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
